import { format } from 'date-fns';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { PromiseToPay } from '@/types/collections';
import type { AllocationRow } from './allocation-workspace';

/** Statuses that represent a promise still owing money and not yet fulfilled (Active, Partially Paid, and Broken). */
export const OUTSTANDING_PROMISE_STATUSES: ReadonlyArray<PromiseToPay['status']> = [
  'Active',
  'Partially Paid',
  'Broken',
];

export interface PromiseAllocationResult {
  transactionId: number;
  amount: number;
}

/** True when the customer has at least one outstanding promise worth opening the modal for. */
export function hasOutstandingPromises(promises: PromiseToPay[]): boolean {
  return promises.some((promise) => OUTSTANDING_PROMISE_STATUSES.includes(promise.status));
}

interface AutoAllocatePromisesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerAccountNumber: string;
  promises: PromiseToPay[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  allocationRows: AllocationRow[];
  getDisplayBalance: (row: AllocationRow) => number;
  /** Receipt's unallocated balance in the current display currency. */
  receiptBalance: number;
  displayCurrency: string;
  formatCurrency: (amount: number, currency?: string) => string;
  onConfirm: (allocations: PromiseAllocationResult[]) => void;
}

interface ModalInvoice {
  transactionId: number;
  reference: string;
  transactionType: string;
  transactionDate: Date;
  dueDate: Date;
  originalAmount: number;
  balance: number;
  promisedAmount: number;
}

interface ModalPromise {
  id: string;
  promiseDate: Date;
  promisedAmount: number;
  outstanding: number;
  fulfilled: number;
  status: PromiseToPay['status'];
  currency?: string;
  invoices: ModalInvoice[];
}

const EPSILON = 0.005;

const INVOICE_GRID_CLASS = 'grid grid-cols-[auto_1.3fr_0.8fr_0.9fr_1fr_1fr_1fr_1.3fr] gap-2';

const round2 = (value: number): number => Math.round(value * 100) / 100;

function statusBadgeVariant(status: PromiseToPay['status']): 'default' | 'secondary' | 'outline' {
  if (status === 'Partially Paid') return 'secondary';
  return 'outline';
}

export function AutoAllocatePromisesModal({
  open,
  onOpenChange,
  customerName,
  customerAccountNumber,
  promises,
  isLoading,
  error,
  onRetry,
  allocationRows,
  getDisplayBalance,
  receiptBalance,
  displayCurrency,
  formatCurrency,
  onConfirm,
}: AutoAllocatePromisesModalProps) {
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [amounts, setAmounts] = useState<Record<number, number>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [focusedPromiseId, setFocusedPromiseId] = useState<string | null>(null);
  const [showUnderAllocationConfirm, setShowUnderAllocationConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const initializedRef = useRef(false);

  const rowById = useMemo(() => {
    const map = new Map<number, AllocationRow>();
    for (const row of allocationRows) {
      map.set(row.transaction.id, row);
    }
    return map;
  }, [allocationRows]);

  // Build the promise → invoice tree. Only outstanding promises and invoice-type transactions
  // that are still allocatable (have a matching workspace row with a positive display balance).
  const tree = useMemo<ModalPromise[]>(() => {
    const result: ModalPromise[] = [];

    for (const promise of promises) {
      if (!OUTSTANDING_PROMISE_STATUSES.includes(promise.status)) continue;

      const seen = new Set<number>();
      const invoices: ModalInvoice[] = [];

      for (const invoice of promise.invoices) {
        if (invoice.transactionId == null) continue;
        const transactionId = Number(invoice.transactionId);
        if (Number.isNaN(transactionId) || seen.has(transactionId)) continue;

        const row = rowById.get(transactionId);
        if (!row || row.transaction.transactionType !== 'invoice') continue;

        const balance = getDisplayBalance(row);
        if (balance <= 0) continue;

        seen.add(transactionId);
        invoices.push({
          transactionId,
          reference: row.transaction.transactionNumber,
          transactionType: row.transaction.transactionType,
          transactionDate: row.transaction.transactionDate,
          dueDate: row.transaction.dueDate,
          originalAmount: row.transaction.originalAmount,
          balance,
          promisedAmount: invoice.promisedAmount,
        });
      }

      const outstanding = promise.invoices.reduce((sum, invoice) => sum + (invoice.balanceRemaining ?? 0), 0);
      result.push({
        id: promise.id,
        promiseDate: promise.promiseDate,
        promisedAmount: promise.promisedAmount,
        outstanding,
        fulfilled: Math.max(0, promise.promisedAmount - outstanding),
        status: promise.status,
        currency: promise.currencyCode,
        invoices,
      });
    }

    return result;
  }, [promises, rowById, getDisplayBalance]);

  // A transaction can be linked to more than one promise; budget/total math must count each
  // transaction once. First occurrence (in tree order) wins so pre-allocation order is stable.
  const uniqueInvoices = useMemo(() => {
    const map = new Map<number, ModalInvoice>();
    for (const promise of tree) {
      for (const invoice of promise.invoices) {
        if (!map.has(invoice.transactionId)) {
          map.set(invoice.transactionId, invoice);
        }
      }
    }
    return [...map.values()];
  }, [tree]);

  // Search (promise id + linked invoice reference) and date sort are display-only: they never
  // touch selection/amounts/budget, which stay keyed by transaction over the full `tree`.
  const visibleTree = useMemo<ModalPromise[]>(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = term
      ? tree.filter(
          (promise) =>
            promise.id.toLowerCase().includes(term) ||
            promise.invoices.some((invoice) => invoice.reference.toLowerCase().includes(term)),
        )
      : tree;

    return [...filtered].sort((a, b) => {
      const diff = new Date(a.promiseDate).getTime() - new Date(b.promiseDate).getTime();
      return sortOrder === 'newest' ? -diff : diff;
    });
  }, [tree, searchTerm, sortOrder]);

  // Budget for this modal = receipt balance minus whatever is already allocated to rows
  // that are not part of this promise tree (so we never over-allocate the receipt as a whole).
  const availableAmount = useMemo(() => {
    const candidateIds = new Set(uniqueInvoices.map((invoice) => invoice.transactionId));

    const otherAllocated = allocationRows
      .filter((row) => !candidateIds.has(row.transaction.id))
      .reduce((sum, row) => sum + row.allocatedAmount, 0);

    return Math.max(0, round2(receiptBalance - otherAllocated));
  }, [uniqueInvoices, allocationRows, receiptBalance]);

  // Fills only newly-selected rows that currently have no amount, in tree order, from the
  // remaining budget. Never touches an amount the user already entered — no auto-rebalancing.
  const fillUnallocated = useCallback(
    (nextSelected: Record<number, boolean>, currentAmounts: Record<number, number>): Record<number, number> => {
      let used = 0;
      for (const invoice of uniqueInvoices) {
        if (nextSelected[invoice.transactionId]) {
          used += currentAmounts[invoice.transactionId] ?? 0;
        }
      }

      let remaining = Math.max(0, availableAmount - used);
      const nextAmounts: Record<number, number> = { ...currentAmounts };

      for (const invoice of uniqueInvoices) {
        if (!nextSelected[invoice.transactionId]) continue;
        if ((nextAmounts[invoice.transactionId] ?? 0) > 0) continue;

        const amount = round2(Math.max(0, Math.min(remaining, invoice.balance)));
        nextAmounts[invoice.transactionId] = amount;
        remaining -= amount;
      }

      return nextAmounts;
    },
    [uniqueInvoices, availableAmount],
  );

  // Initialise selection/amounts once per open, after data has loaded: everything selected,
  // pre-allocated exactly like the existing auto-allocate order.
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setShowUnderAllocationConfirm(false);
      setSearchTerm('');
      setSortOrder('newest');
      return;
    }
    if (initializedRef.current || isLoading) return;

    const nextSelected: Record<number, boolean> = {};
    const nextExpanded: Record<string, boolean> = {};
    for (const promise of tree) {
      nextExpanded[promise.id] = true;
      for (const invoice of promise.invoices) {
        nextSelected[invoice.transactionId] = true;
      }
    }

    setSelected(nextSelected);
    setAmounts(fillUnallocated(nextSelected, {}));
    setExpanded(nextExpanded);
    setFocusedPromiseId(tree[0]?.id ?? null);
    initializedRef.current = true;
  }, [open, isLoading, tree, fillUnallocated]);

  const selectedInvoices = useMemo(() => {
    return uniqueInvoices
      .filter((invoice) => selected[invoice.transactionId])
      .map((invoice) => ({ transactionId: invoice.transactionId, amount: amounts[invoice.transactionId] ?? 0 }));
  }, [uniqueInvoices, selected, amounts]);

  const totalAllocated = useMemo(
    () => round2(selectedInvoices.reduce((sum, item) => sum + item.amount, 0)),
    [selectedInvoices],
  );

  const selectedInvoiceCount = selectedInvoices.filter((item) => item.amount > 0).length;
  const selectedPromiseCount = useMemo(
    () => tree.filter((promise) => promise.invoices.some((invoice) => selected[invoice.transactionId])).length,
    [tree, selected],
  );

  const remaining = round2(availableAmount - totalAllocated);
  const isOverAllocated = totalAllocated > availableAmount + EPSILON;
  const isUnderAllocated = totalAllocated < availableAmount - EPSILON;
  const confirmDisabled = selectedInvoiceCount === 0 || isOverAllocated;

  const promiseCheckState = useCallback(
    (promise: ModalPromise): boolean | 'indeterminate' => {
      if (promise.invoices.length === 0) return false;
      const selectedCount = promise.invoices.filter((invoice) => selected[invoice.transactionId]).length;
      if (selectedCount === 0) return false;
      if (selectedCount === promise.invoices.length) return true;
      return 'indeterminate';
    },
    [selected],
  );

  const togglePromise = useCallback(
    (promise: ModalPromise, checked: boolean) => {
      setFocusedPromiseId(promise.id);
      const next = { ...selected };
      for (const invoice of promise.invoices) {
        next[invoice.transactionId] = checked;
      }
      setSelected(next);
      if (checked) {
        setAmounts((prevAmounts) => fillUnallocated(next, prevAmounts));
      }
    },
    [selected, fillUnallocated],
  );

  const toggleInvoice = useCallback(
    (invoice: ModalInvoice, checked: boolean) => {
      const next = { ...selected, [invoice.transactionId]: checked };
      setSelected(next);
      if (checked) {
        setAmounts((prevAmounts) => fillUnallocated(next, prevAmounts));
      }
    },
    [selected, fillUnallocated],
  );

  const capForInvoice = useCallback(
    (invoice: ModalInvoice): number => {
      const currentAmount = selected[invoice.transactionId] ? (amounts[invoice.transactionId] ?? 0) : 0;
      const otherSelectedTotal = totalAllocated - currentAmount;
      return Math.max(0, round2(Math.min(invoice.balance, availableAmount - otherSelectedTotal)));
    },
    [selected, amounts, totalAllocated, availableAmount],
  );

  const handleAmountChange = useCallback((transactionId: number, value: number | null) => {
    setAmounts((prev) => ({ ...prev, [transactionId]: round2(Math.max(0, value ?? 0)) }));
  }, []);

  // Report every transaction the modal manages so the workspace can also clear invoices the
  // user deselected (amount 0), not just apply the selected ones.
  const applyAndClose = useCallback(() => {
    const result: PromiseAllocationResult[] = uniqueInvoices.map((invoice) => ({
      transactionId: invoice.transactionId,
      amount: selected[invoice.transactionId] ? round2(Math.max(0, amounts[invoice.transactionId] ?? 0)) : 0,
    }));
    onConfirm(result);
    onOpenChange(false);
  }, [uniqueInvoices, selected, amounts, onConfirm, onOpenChange]);

  // Re-validate from state (never trust the button's disabled flag alone).
  const handleConfirm = useCallback(() => {
    const activeInvoices = selectedInvoices.filter((item) => item.amount > 0);
    const total = round2(activeInvoices.reduce((sum, item) => sum + item.amount, 0));

    if (activeInvoices.length === 0 || total > availableAmount + EPSILON) return;
    if (total < availableAmount - EPSILON) {
      setShowUnderAllocationConfirm(true);
      return;
    }
    applyAndClose();
  }, [selectedInvoices, availableAmount, applyAndClose]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-sm">Loading promises…</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <span className="text-sm text-muted-foreground">Failed to load promises for this customer.</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      );
    }

    if (tree.length === 0) {
      return (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          No outstanding promises found for this customer
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="sticky top-0 z-10 flex flex-col gap-2 bg-background pb-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search promise or invoice reference…"
              className="h-9 pl-8 text-sm"
            />
          </div>
          <Select value={sortOrder} onValueChange={(value) => setSortOrder(value as 'newest' | 'oldest')}>
            <SelectTrigger className="h-9 w-full text-sm sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Promise date (newest)</SelectItem>
              <SelectItem value="oldest">Promise date (oldest)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {visibleTree.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No promises match your search.</div>
        ) : (
          visibleTree.map((promise) => {
            const isExpanded = expanded[promise.id] ?? true;
            const isFocused = focusedPromiseId === promise.id;
            const checkState = promiseCheckState(promise);
            const promiseCurrency = promise.currency ?? displayCurrency;

            return (
              <div
                key={promise.id}
                className={cn(
                  'rounded-md border transition-colors',
                  isFocused ? 'border-teal-400 bg-teal-50/50' : 'border-border',
                )}
              >
                {/* Promise header row */}
                <div
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                  onClick={() => {
                    setFocusedPromiseId(promise.id);
                    setExpanded((prev) => ({ ...prev, [promise.id]: !(prev[promise.id] ?? true) }));
                  }}
                >
                  <Checkbox
                    checked={checkState}
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={(value) => togglePromise(promise, value === true)}
                    aria-label={`Select promise ${promise.id}`}
                  />
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="font-semibold text-sm">Promise #{promise.id}</span>
                    <span className="text-muted-foreground">{format(new Date(promise.promiseDate), 'dd/MM/yyyy')}</span>
                    <span>
                      <span className="text-muted-foreground">Promised: </span>
                      {formatCurrency(promise.promisedAmount, promiseCurrency)}
                    </span>
                    <span>
                      <span className="text-muted-foreground">Fulfilled: </span>
                      {formatCurrency(promise.fulfilled, promiseCurrency)}
                    </span>
                    <span>
                      <span className="text-muted-foreground">Outstanding: </span>
                      {formatCurrency(promise.outstanding, promiseCurrency)}
                    </span>
                  </div>
                  <Badge variant={statusBadgeVariant(promise.status)} className="shrink-0">
                    {promise.status}
                  </Badge>
                </div>

                {/* Invoice children */}
                {isExpanded && (
                  <div className="border-t bg-muted/20 px-3 py-2">
                    <div className="mb-1 text-xs font-medium text-muted-foreground">
                      Transactions for Promise #{promise.id}
                    </div>
                    {promise.invoices.length === 0 ? (
                      <div className="py-3 text-xs text-muted-foreground">No transactions found for this promise.</div>
                    ) : (
                      <div className="space-y-1">
                        <div
                          className={cn(
                            INVOICE_GRID_CLASS,
                            'px-2 pb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide',
                          )}
                        >
                          <span />
                          <span>Reference</span>
                          <span>Type</span>
                          <span>Due</span>
                          <span className="text-right">Original</span>
                          <span className="text-right">Balance</span>
                          <span className="text-right">Promised</span>
                          <span className="text-right">Allocated</span>
                        </div>
                        {promise.invoices.map((invoice) => {
                          const isChecked = selected[invoice.transactionId] ?? false;
                          return (
                            <div
                              key={invoice.transactionId}
                              className={cn(
                                INVOICE_GRID_CLASS,
                                'items-center rounded px-2 py-1.5 text-xs',
                                isChecked ? 'bg-background' : 'opacity-50',
                              )}
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={(value) => toggleInvoice(invoice, value === true)}
                                aria-label={`Select transaction ${invoice.reference}`}
                              />
                              <span className="font-medium">{invoice.reference}</span>
                              <span className="capitalize text-muted-foreground">{invoice.transactionType}</span>
                              <span className="text-muted-foreground">
                                {format(new Date(invoice.dueDate), 'dd/MM/yyyy')}
                              </span>
                              <span className="text-right text-muted-foreground">
                                {formatCurrency(invoice.originalAmount, displayCurrency)}
                              </span>
                              <span className="text-right">{formatCurrency(invoice.balance, displayCurrency)}</span>
                              <span className="text-right text-muted-foreground">
                                {formatCurrency(invoice.promisedAmount, displayCurrency)}
                              </span>
                              <div className="flex justify-end">
                                <CurrencyInput
                                  currencyCode={displayCurrency}
                                  value={amounts[invoice.transactionId] ?? 0}
                                  min={0}
                                  max={capForInvoice(invoice)}
                                  disabled={!isChecked}
                                  onChange={(value) => handleAmountChange(invoice.transactionId, value)}
                                  className="h-8 w-28 text-right text-xs"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-4xl">
          <DialogHeader className="border-b p-6 pb-4">
            <DialogTitle>Auto-Allocate Promises</DialogTitle>
            <DialogDescription>
              {customerName}
              {customerAccountNumber ? ` · ${customerAccountNumber}` : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 py-4">{renderBody()}</div>

          <DialogFooter className="flex-col gap-3 border-t p-6 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-0.5 text-xs">
              <div className="flex flex-wrap gap-x-4">
                <span className="text-muted-foreground">
                  {selectedPromiseCount} promise{selectedPromiseCount === 1 ? '' : 's'} · {selectedInvoiceCount}{' '}
                  transaction{selectedInvoiceCount === 1 ? '' : 's'}
                </span>
                <span>
                  <span className="text-muted-foreground">Allocating: </span>
                  <span className="font-medium">{formatCurrency(totalAllocated, displayCurrency)}</span>
                  <span className="text-muted-foreground"> of {formatCurrency(availableAmount, displayCurrency)}</span>
                </span>
              </div>
              {isOverAllocated && (
                <span className="font-medium text-amber-600">
                  Total exceeds the receipt balance by {formatCurrency(Math.abs(remaining), displayCurrency)}
                </span>
              )}
              {isUnderAllocated && selectedInvoiceCount > 0 && (
                <span className="text-amber-600">
                  Receipt will not be fully allocated ({formatCurrency(remaining, displayCurrency)} remaining)
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={confirmDisabled}>
                Confirm Allocation
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showUnderAllocationConfirm} onOpenChange={setShowUnderAllocationConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receipt not fully allocated</DialogTitle>
            <DialogDescription>
              This will allocate {formatCurrency(totalAllocated, displayCurrency)} of{' '}
              {formatCurrency(availableAmount, displayCurrency)}, leaving {formatCurrency(remaining, displayCurrency)}{' '}
              unallocated. Do you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnderAllocationConfirm(false)}>
              No
            </Button>
            <Button
              onClick={() => {
                setShowUnderAllocationConfirm(false);
                applyAndClose();
              }}
            >
              Yes, continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
